import React, { useState } from 'react';
import { get } from 'lodash';
import styled from 'styled-components';
import dayjs from 'utils/dayjs';

import { FISCAL_PERIOD_TYPE, GROUPING } from 'utils/fiscalPeriods';
import { ISO_DATETIME } from 'utils/dates';
import messages from './messages';
import { MAX_CUSTOM_PERIOD_LENGTH } from './constants';

import { FormattedMessage } from 'react-intl';
import { Modal, Form } from 'antd';
import DatePicker from 'components/DatePicker';
import Button from 'components/forms/Button';
import Select from 'components/forms/Select';
import Radio from 'components/forms/Radio';
import PeriodFormItem from './PeriodFormItem';

const { Option } = Select;

const PresetsContainer = styled.div`
    > *:not(:first-child) {
        margin-left: 15px;
    }
`;

const PeriodSelectionModal = ({
    visible,
    onConfirm,
    onCancel,
    periods,
    hidePointInTime = false,
    maxDateRange = MAX_CUSTOM_PERIOD_LENGTH,
}) => {
    const [form] = Form.useForm();
    const [year, setYear] = useState(dayjs());
    const [grouping, setGrouping] = useState(GROUPING.MONTHLY);
    const [periodType, setPeriodType] = useState(FISCAL_PERIOD_TYPE.PREDEFINED);
    const [pointInTime, setPointInTime] = useState(dayjs().endOf('day').format(ISO_DATETIME));

    /**
     * Resets the period depending on selected year/grouping/periodType
     * e.g. if we change the year, then a custom period should be reset to the first month of the new year, while pre-defined period reset to null (nothing selected)
     */
    const resetPeriod = (newValues) => {
        if (get(newValues, 'periodType', periodType) === FISCAL_PERIOD_TYPE.CUSTOM) {
            const yearToUse = get(newValues, 'year', year);
            form.setFieldsValue({
                period: [yearToUse.startOf('year').startOf('month'), yearToUse.startOf('year').endOf('month')],
            });
        } else {
            form.setFieldsValue({ period: null });
        }
    };

    /**
     * State reset when opening/closing modal
     */
    const resetAllFields = () => {
        form.resetFields();
        setYear(dayjs());
        setGrouping(GROUPING.MONTHLY);
        setPeriodType(FISCAL_PERIOD_TYPE.PREDEFINED);
        setPointInTime(dayjs().endOf('day').format(ISO_DATETIME));
    };

    const onCancelModal = () => {
        resetAllFields();
        onCancel();
    };

    const onConfirmModal = () => {
        form.validateFields()
            .then((values) => {
                resetAllFields();

                let startDate;
                let endDate;
                const details = { year: year.year(), type: periodType };
                if (periodType === FISCAL_PERIOD_TYPE.PREDEFINED) {
                    // periods will always be loaded if we reach this point as a period was selected
                    const selectedPeriod = periods.data.find((row) => row.id === values.period);
                    ({ startDate, endDate } = selectedPeriod);
                    details.id = selectedPeriod.id;
                    details.name = selectedPeriod.name;
                    details.grouping = grouping;
                } else {
                    startDate = values.period[0].format('YYYY-MM-DD');
                    endDate = values.period[1].format('YYYY-MM-DD');
                }
                onConfirm(startDate, endDate, details, pointInTime);
            })
            .catch(() => {});
    };

    const onChangeYear = (date) => {
        const value = date;
        setYear(value);
        resetPeriod({ year: value });
    };

    const onChangeGrouping = (grp) => {
        setGrouping(grp);
        resetPeriod({ grouping: grp });
    };

    const onChangePeriodType = (event) => {
        const type = event.target.value;
        setPeriodType(type);
        resetPeriod({ periodType: type });
    };

    const onChangePointInTime = (date) => {
        setPointInTime(date.endOf('day').format(ISO_DATETIME));
    };

    const getGroupingLabel = (grp) => {
        switch (grp) {
            case GROUPING.WEEKLY:
                return <FormattedMessage {...messages.weeklyGroupingLabel} />;
            case GROUPING.QUARTERLY:
                return <FormattedMessage {...messages.quarterlyGroupingLabel} />;
            case GROUPING.MONTHLY:
            default:
                return <FormattedMessage {...messages.monthlyGroupingLabel} />;
        }
    };

    const renderPointInTimePickerPresets = () => {
        const setToToday = () => onChangePointInTime(dayjs());
        const setToLYSPIT = () => onChangePointInTime(dayjs().subtract(52, 'weeks'));

        return (
            <PresetsContainer>
                <Button type="default" size="small" onClick={setToToday}>
                    <FormattedMessage {...messages.pitTodayPresetLabel} />
                </Button>
                <Button type="default" size="small" onClick={setToLYSPIT}>
                    <FormattedMessage {...messages.pitLySpitPresetLabel} />
                </Button>
            </PresetsContainer>
        );
    };

    return (
        <Modal
            maskClosable={false}
            closable={false}
            centered
            open={visible}
            title={<FormattedMessage {...messages.title} />}
            footer={[
                <Button key="cancel" name="cancelBtn" onClick={onCancelModal}>
                    <FormattedMessage {...messages.cancelButton} />
                </Button>,
                <Button key="submit" name="submitBtn" type="primary" onClick={onConfirmModal}>
                    <FormattedMessage {...messages.confirmButton} />
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                name="source_selection_form"
                initialValues={{
                    period: null,
                }}
            >
                <Form.Item label={<FormattedMessage {...messages.yearFieldLabel} />} required>
                    <DatePicker
                        key="modalPeriodSelectionYear"
                        allowClear={false}
                        picker="year"
                        value={year}
                        onChange={onChangeYear}
                    />
                </Form.Item>
                <Form.Item label={<FormattedMessage {...messages.periodTypeFieldLabel} />} required>
                    <Radio.Group value={periodType} onChange={onChangePeriodType}>
                        <Radio value={FISCAL_PERIOD_TYPE.PREDEFINED}>
                            <FormattedMessage {...messages.predefinedPeriodOptionLabel} />
                        </Radio>
                        <Radio value={FISCAL_PERIOD_TYPE.CUSTOM}>
                            <FormattedMessage {...messages.customPeriodOptionLabel} />
                        </Radio>
                    </Radio.Group>
                </Form.Item>
                {periodType === FISCAL_PERIOD_TYPE.PREDEFINED && (
                    <Form.Item label={<FormattedMessage {...messages.groupingFieldLabel} />} required>
                        <FormattedMessage {...messages.groupingByLabel} />
                        <Select
                            name="groupingSelector"
                            value={grouping}
                            style={{ width: 'auto', marginLeft: '10px' }}
                            onChange={onChangeGrouping}
                        >
                            {Object.values(GROUPING).map((grp) => (
                                <Option key={grp} value={grp}>
                                    {getGroupingLabel(grp)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                <PeriodFormItem
                    fieldName="period"
                    form={form}
                    year={year.year()}
                    grouping={grouping}
                    periodType={periodType}
                    periods={periods}
                    maxDateRange={maxDateRange}
                />
                {!hidePointInTime && (
                    <Form.Item label={<FormattedMessage {...messages.pointInTimeFieldLabel} />} required>
                        <DatePicker
                            name="pitPicker"
                            picker="date"
                            allowClear={false}
                            showToday={false}
                            value={dayjs(pointInTime)}
                            onChange={onChangePointInTime}
                            renderExtraFooter={renderPointInTimePickerPresets}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default PeriodSelectionModal;
